import React, { useState } from "react";

const MODE = {
  product: { label: "웹에서 상품 확인", suffix: "" },
  hs: { label: "웹 HS 사례 교차확인", suffix: " HS code" },
  image: { label: "이미지로 외형 확인", suffix: "", image: true },
};

const EVENT_BY_MODE = {
  product: "product_web_search",
  image: "product_image_search",
  hs: "hs_external_crosscheck",
};

export const GOOGLE_SEARCH_OPEN_MODE_KEY = "google-search-open-mode";
export const IMAGE_CHECK_KEY = "search-image-check";

/**
 * Whether a search should bring image evidence along.
 *
 * <p>On with no attachment, the user checks the goods against Google Images — free, instant, and
 * their own eyes do the work. On with an attachment, the photo goes to vision recognition instead.
 * Off means no image step at all. One control, because "free Google" and "paid AI" being separate
 * buttons made it impossible to tell from the screen which one costs money.
 */
export function getImageCheckEnabled() {
  try {
    return window.localStorage.getItem(IMAGE_CHECK_KEY) !== "off";
  } catch (_) {
    return true;
  }
}

export function setImageCheckEnabled(enabled) {
  try {
    window.localStorage.setItem(IMAGE_CHECK_KEY, enabled ? "on" : "off");
  } catch (_) { /* private mode: the in-memory state still drives this session */ }
}

export function getGoogleSearchOpenMode() {
  try {
    return window.localStorage.getItem(GOOGLE_SEARCH_OPEN_MODE_KEY) === "popup" ? "popup" : "tab";
  } catch (_) {
    return "tab";
  }
}

function saveGoogleSearchOpenMode(mode) {
  try {
    window.localStorage.setItem(GOOGLE_SEARCH_OPEN_MODE_KEY, mode);
  } catch (_) { /* private mode/quota: falls back to in-memory state only */ }
}

export function buildGoogleSearchUrl(query, mode = "product") {
  const config = MODE[mode] || MODE.product;
  const search = `${String(query || "").trim()}${config.suffix}`.trim();
  const params = new URLSearchParams({ q: search });
  if (config.image) params.set("tbm", "isch");
  return `https://www.google.com/search?${params.toString()}`;
}

// Search terms may contain customer or transaction data. Track only the action
// and UI context; never forward the raw query to analytics.
export function trackGoogleSearchAction(mode, context = "unknown") {
  try {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return false;
    window.gtag("event", EVENT_BY_MODE[mode] || "external_search", {
      search_context: context,
      transport_type: "beacon",
    });
    return true;
  } catch (_) {
    return false;
  }
}

export function openGoogleSearchPopup(query, mode = "product", browser = window) {
  const width = Math.max(420, Math.min(1180, (browser.screen?.availWidth || 1220) - 40));
  const height = Math.max(520, Math.min(860, (browser.screen?.availHeight || 900) - 40));
  const outerLeft = Number(browser.screenLeft ?? browser.screenX ?? 0);
  const outerTop = Number(browser.screenTop ?? browser.screenY ?? 0);
  const viewportWidth = Number(browser.outerWidth || browser.screen?.availWidth || width);
  const viewportHeight = Number(browser.outerHeight || browser.screen?.availHeight || height);
  const left = Math.max(0, Math.round(outerLeft + (viewportWidth - width) / 2));
  const top = Math.max(0, Math.round(outerTop + (viewportHeight - height) / 2));
  const name = "loginhsGoogleSearch";
  const features = `popup=yes,width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`;
  const popup = browser.open(buildGoogleSearchUrl(query, mode), name, features);
  if (!popup) return false;
  try { popup.opener = null; } catch (_) { /* cross-browser best effort */ }
  try { popup.focus(); } catch (_) { /* focus can be denied by the browser */ }
  return true;
}

function GoogleLink({ query, mode, openMode, label, primary = false, eventContext }) {
  const href = buildGoogleSearchUrl(query, mode);
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={(event) => {
        trackGoogleSearchAction(mode, eventContext);
        if (openMode === "popup" && openGoogleSearchPopup(query, mode)) event.preventDefault();
      }}
      style={{
        border: `1px solid ${primary ? "#2563eb" : "#cbd5e1"}`,
        borderRadius: 7,
        background: primary ? "#2563eb" : "#fff",
        color: primary ? "#fff" : "#1d4ed8",
        padding: "7px 10px",
        fontSize: 11.5,
        fontWeight: 750,
        textDecoration: "none",
        whiteSpace: "nowrap",
      }}
    >
      {label || MODE[mode]?.label || MODE.product.label} ↗
    </a>
  );
}

export default function GoogleSearchActions({
  query,
  compact = false,
  modes = ["product", "image"],
  labels = {},
  eventContext = "product_identity",
  showModeToggle = true,
}) {
  const [openMode, setOpenMode] = useState(getGoogleSearchOpenMode);

  const toggleOpenMode = () => {
    const next = openMode === "popup" ? "tab" : "popup";
    saveGoogleSearchOpenMode(next);
    setOpenMode(next);
  };

  return (
    <div aria-label="Google 직접 검색" style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", justifyContent: compact ? "flex-end" : "flex-start" }}>
      {modes.map((mode, index) => (
        <GoogleLink
          key={mode}
          query={query}
          mode={mode}
          openMode={openMode}
          primary={index === 0 && mode === "product"}
          label={labels[mode] || MODE[mode]?.label}
          eventContext={eventContext}
        />
      ))}
      {showModeToggle && <button
        type="button"
        onClick={toggleOpenMode}
        title={openMode === "popup"
          ? "지금은 위치 고정 팝업으로 열립니다. 클릭하면 새 탭으로 전환합니다."
          : "지금은 새 탭으로 열립니다. 클릭하면 위치 고정 팝업으로 전환합니다."}
        style={{
          border: "1px solid #cbd5e1",
          borderRadius: 7,
          background: "#f8fafc",
          color: "#475569",
          padding: "7px 10px",
          fontSize: 11,
          fontWeight: 600,
          whiteSpace: "nowrap",
          cursor: "pointer",
        }}
      >
        {openMode === "popup" ? "고정 팝업으로 열림" : "새 탭으로 열림"}
      </button>}
    </div>
  );
}

/**
 * Free image check for the searched term.
 *
 * <p>Google results are linked, never embedded — their terms do not allow rendering them inside
 * another page, and a link costs nothing while the API does. It also keeps the honest limit visible:
 * this confirms what the *word* usually means (4202 vs 4205), not what *this* shipment is made of.
 * Leather and a good synthetic are indistinguishable in a photo, and that is exactly where the duty
 * rate splits.
 */
export function GoogleImageCheckCard({ query, recognized = "" }) {
  const term = String(query || "").trim();
  if (!term) return null;
  return (
    <div style={{ border: "1px solid #dbe3ec", borderRadius: 8, background: "#fff", padding: "10px 12px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <strong style={{ fontSize: 12.5, color: "#334155" }}>외형 확인</strong>
        <a
          href={buildGoogleSearchUrl(term, "image")}
          target="_blank"
          rel="noreferrer"
          onClick={(event) => {
            trackGoogleSearchAction("image", "image_check_card");
            if (openGoogleSearchPopup(term, "image")) event.preventDefault();
          }}
          style={{ border: "1px solid #cbd5e1", borderRadius: 7, background: "#f8fafc", color: "#1d4ed8", padding: "6px 10px", fontSize: 11.5, fontWeight: 700, textDecoration: "none" }}
          title={buildGoogleSearchUrl(term, "image")}
        >
          「{term}」 이미지로 보기 ↗
        </a>
        <span style={{ fontSize: 11, color: "#94a3b8" }}>무료 · 외부 링크</span>
      </div>
      {recognized && (
        <div style={{ marginTop: 6, fontSize: 11.5, color: "#475569" }}>
          첨부 이미지 인식: <strong>{recognized}</strong>
        </div>
      )}
      <div style={{ marginTop: 6, fontSize: 11, lineHeight: 1.5, color: "#94a3b8" }}>
        외형은 호(4자리) 확인에 쓰고, 재질·조성처럼 세번을 가르는 사실은 사진으로 판단하지 않습니다.
      </div>
    </div>
  );
}

export function GoogleHsCrosscheck({ query, hsk, compact = false }) {
  const searchQuery = [String(query || "").trim(), String(hsk || "").replace(/\D/g, "")]
    .filter(Boolean)
    .join(" ");
  if (!searchQuery) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <GoogleSearchActions
        query={searchQuery}
        compact={compact}
        modes={["hs"]}
        eventContext="classification_result"
        showModeToggle={false}
      />
      <div style={{ marginTop: 5, color: "var(--audit-text-muted, #64748b)", fontSize: 11.5 }}>
        외부 웹 검색은 교차확인용이며 공식 세번 판정이 아닙니다.
      </div>
    </div>
  );
}
